import * as React from "react";
import { keyBy } from "lodash";
import { PlayerPost, ReviewStatus } from "@/interfaces";

import { PostServices } from "@/services";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import { useSocialNetworks } from "@/utils";
import { Button, Dialog } from "@/components/ui";

import { ComplianceBubbleDetails } from "./components/BubbleDetails";
import { modalReducer } from "./modalReducer";
import useDisplayData from "./hook";

interface OwnProps {
  opened: boolean;
  playerId: number;
  selectedPost: PlayerPost;
  thumbnail: string;
  closeHandler: () => void;
}

type Props = OwnProps;

/**
 * ComplianceModal Components
 */
const ComplianceModalFC: React.FC<Props> = ({ opened, selectedPost, closeHandler, playerId, thumbnail }) => {
  const [state, dispatch] = React.useReducer(modalReducer, { open: opened, loading: false });

  const { postReviewEvents, message, successSocialNetworks, changesRequested, postReviewEventsLabel, postedDate } =
    useDisplayData(selectedPost, playerId);

  const postSnIds = successSocialNetworks?.map(sn => sn.socialNetworkId);

  const { postableSocialNetworks } = useSocialNetworks();

  React.useEffect(() => {
    if (opened) {
      dispatch({ type: "open" });
    } else {
      dispatch({ type: "close" });
    }
  }, [opened]);

  const markAsResolved = (): void => {
    PostServices.setReviewStatus(selectedPost.postId, ReviewStatus.CHANGES_SUBMITTED)
      .then(close)
      .catch(e => alert(e.message));
  };

  const close = () => {
    dispatch({ type: "close" });
    closeHandler();
  };

  const links = React.useMemo(() => {
    const linksArray = successSocialNetworks.map(sn => {
      return {
        socialNetworkId: sn.socialNetworkId,
        externalLink: sn.externalLink,
      };
    });

    const grouped = keyBy(linksArray, "socialNetworkId");
    let linksGrouped = {};
    Object.keys(grouped).forEach(sn => {
      linksGrouped = {
        ...linksGrouped,
        [sn]: grouped[sn]["externalLink"],
      };
    });
    return linksGrouped;
  }, [successSocialNetworks]);

  return (
    <Dialog open={state.open} onOpenChange={open => !open && close()} size="lg" showCloseButton>
      <Dialog.Header>
        <Dialog.Title>Compliance Review</Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-5 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1">
        {/* Upper Content Box */}
        <div className="flex flex-row gap-5 items-start justify-between w-full min-w-0">
          <div className="flex flex-col justify-between flex-1 min-w-0 min-h-[160px] text-left">
            <p className="text-slate-800 text-sm font-medium leading-relaxed whitespace-pre-wrap line-clamp-[6] max-h-[12em] overflow-hidden">
              {message}
            </p>
            {postedDate && <p className="text-xs text-slate-400 font-semibold mt-2">{"Posted " + postedDate}</p>}
          </div>
          {thumbnail && (
            <div className="w-36 h-48 shrink-0 rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
              <img src={thumbnail} alt="Post preview" className="size-full object-cover" />
            </div>
          )}
        </div>

        {/* Compliance Chat Events Section */}
        {postReviewEvents && (
          <div className="flex flex-col gap-2 pt-4 border-t border-slate-100 text-left">
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider">{postReviewEventsLabel}</h5>
            <ComplianceBubbleDetails messages={postReviewEvents} />
          </div>
        )}

        {/* Social Links Section */}
        {successSocialNetworks && successSocialNetworks.length > 0 && (
          <div className="flex flex-col gap-3 pt-4 border-t border-slate-100 text-left">
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Review Your Posts</h5>
            <p className="text-xs text-slate-500 leading-normal">
              Click the highlighted icons to edit or delete the posts related to this review. If a link doesn't work,
              you may need to find the post manually.
            </p>
            <div className="flex justify-start py-1">
              <SocialNetworksSection
                size="big"
                actives={postSnIds}
                links={links}
                allSocialNetworks={postableSocialNetworks}
              />
            </div>
            <p className="text-xs text-slate-500 leading-normal">
              When complete, tap the 'Mark as Resolved' button below to confirm that this change has been made.
            </p>
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer showCloseButton>
        {changesRequested && (
          <Button variant="default" onClick={markAsResolved} disabled={state.loading}>
            Mark as Resolved
          </Button>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};

export const ComplianceModal = ComplianceModalFC;
